import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Eye, Loader2, Printer, FileText } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePrintForm } from "./usePrintForm";
import { Field, Segmented, TemplatePicker, RegionPicker, PatientPicker, SourcePicker, ResultPicker } from "./PrintFormSettings";

function ExportSummary({ exp }) {
  if (!exp) return null;
  const c = exp.coverage;
  return (
    <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 text-2xs text-zinc-600 space-y-1" data-testid="print-export-summary">
      <div>
        <b className="text-zinc-900">{exp.filename}</b> · {exp.mode_label} · generated {exp.generated_local}
        {exp.source && <> · source: {exp.source.template_name} ({exp.source.submitted_local})</>}
      </div>
      <div data-testid="print-export-coverage">
        {exp.mode === "completed"
          ? <>{c.rendered} fields printed · {c.answered} recorded answers</>
          : <>{c.rendered} fields printed · {c.prefilled} pre-filled [P] · {c.report_blocks} patient-report blocks
            · {c.withheld} clinician-only left blank{c.results ? ` · ${c.results} scored-result fields [R]` : ""}</>}
        {" "}· {c.deduplicated.length} cross-referenced · {c.excluded.length} retired
      </div>
      {exp.result && (
        <div data-testid="print-export-result">
          <b>Scored result [R]:</b> {exp.result.instrument}{exp.result.version ? ` v${exp.result.version}` : ""} · {exp.result.summary} · dated {exp.result.date_local}
        </div>
      )}
      {exp.record && (
        <div data-testid="print-export-record">
          <b>Finalised</b> by {exp.record.finalised_by} on {exp.record.finalised_local}
          {exp.record.amendments ? ` · amended ${exp.record.amendments}×` : ""}
        </div>
      )}
      {exp.safety_box?.length > 0 && (
        <div className="text-zinc-900" data-testid="print-export-safety">
          <b>Review items:</b> {exp.safety_box.join(" · ")}
        </div>
      )}
    </div>
  );
}

function PreviewPane({ pf, frameRef }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col bg-zinc-100">
      <ExportSummary exp={pf.exp} />
      {pf.pdfUrl ? (
        <iframe ref={frameRef} src={pf.pdfUrl} title="Assessment form preview" className="flex-1 w-full bg-white"
          data-testid="print-preview-frame" />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-10 text-zinc-500" data-testid="print-preview-empty">
          <FileText className="h-10 w-10 text-zinc-300 mb-3" />
          <div className="text-sm font-medium text-zinc-700">No preview yet</div>
          <div className="text-xs mt-1 max-w-xs">
            Choose your settings and press <b>Preview</b>. Printing uses the exact same generated PDF.
            Nothing is saved to the assessment.
          </div>
        </div>
      )}
    </div>
  );
}

function Actions({ pf, frameRef }) {
  const off = !!pf.blocker || !!pf.busy;
  const spin = (k, Icon) => (pf.busy === k ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Icon className="h-3.5 w-3.5 mr-1.5" />);
  return (
    <div className="space-y-2 pt-3 border-t border-zinc-200">
      {pf.blocker && <div className="text-2xs text-zinc-600" data-testid="print-blocker">{pf.blocker}</div>}
      {pf.error && (
        <div className="text-xs rounded-md border border-red-200 bg-red-50 text-red-800 px-2.5 py-1.5" role="alert" data-testid="print-error">
          {pf.error}
        </div>
      )}
      <div className="grid grid-cols-3 gap-1.5">
        <Button size="sm" variant="outline" disabled={off} onClick={pf.preview} data-testid="print-preview-btn">
          {spin("preview", Eye)} Preview
        </Button>
        <Button size="sm" variant="outline" disabled={off} onClick={pf.download} data-testid="print-download-btn">
          {spin("download", Download)} Download
        </Button>
        <Button size="sm" className="bg-zinc-950 hover:bg-zinc-800 text-white" disabled={off || !pf.pdfUrl}
          onClick={() => pf.requestPrint(frameRef.current)} data-testid="print-print-btn"
          title={pf.pdfUrl ? "Print the previewed PDF" : "Preview the PDF first"}>
          {spin("print", Printer)} Print
        </Button>
      </div>
    </div>
  );
}

function Settings({ pf, clientName }) {
  const { s, update, sources, email, ctx } = pf;
  const prefillReason = !email ? "Open from a client's record to pre-fill" : sources?.disabled_reason || "";
  const completedReason = ctx && ctx.status !== "finalised" ? "Finalise to print the completed record" : "";
  const modes = [
    { value: "blank", label: "Blank assessment", hint: "No answers inserted", testid: "print-mode-blank" },
    { value: "onboarding_prefill", label: "Pre-filled from onboarding", hint: "Patient answers marked [P]",
      testid: "print-mode-prefill", disabled: !!prefillReason, title: prefillReason },
  ];
  if (ctx) {
    modes.push({ value: "completed", label: "Completed record", hint: "Saved answers + review marks, for filing",
      testid: "print-mode-completed", disabled: !!completedReason, title: completedReason });
  }
  return (
    <div className="space-y-4">
      <TemplatePicker pf={pf} />
      <RegionPicker pf={pf} />
      <Field label="Print mode">
        <Segmented value={s.mode} onChange={(v) => update({ mode: v })} options={modes} />
        {prefillReason && <div className="text-2xs text-zinc-500" data-testid="print-prefill-disabled">{prefillReason}</div>}
        {completedReason && <div className="text-2xs text-zinc-500" data-testid="print-completed-disabled">{completedReason}</div>}
      </Field>
      <PatientPicker pf={pf} clientName={clientName} />
      <SourcePicker pf={pf} />
      <ResultPicker pf={pf} />
      <Field label="Writing space">
        <Segmented value={s.writing} onChange={(v) => update({ writing: v })} options={[
          { value: "compact", label: "Compact", hint: "Default spacing", testid: "print-writing-compact" },
          { value: "extra", label: "Extra writing space", hint: "Taller lines & more rows", testid: "print-writing-extra" },
        ]} />
      </Field>
    </div>
  );
}

export function PrintFormDialog({ open, onOpenChange, assessmentId, clientEmail, clientName, canonicalClientId, defaultTemplateKey }) {
  const pf = usePrintForm({ open, assessmentId, clientEmail, clientName, canonicalClientId, defaultTemplateKey });
  const frameRef = useRef(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="print-form-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-200 shrink-0">
          <DialogTitle className="flex items-center gap-2"><Printer className="h-4 w-4" /> Print / PDF assessment form</DialogTitle>
          <DialogDescription>A4 paper form for completing by hand. Generating it never changes the assessment.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 min-h-0">
          <div className="w-[340px] shrink-0 border-r border-zinc-200 flex flex-col">
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {pf.metaError && <div className="text-xs text-red-700" data-testid="print-meta-error">{pf.metaError}</div>}
              {!pf.meta && !pf.metaError && <div className="text-sm text-zinc-500">Loading templates…</div>}
              {pf.meta && <Settings pf={pf} clientName={clientName} />}
            </div>
            <div className="px-5 pb-4 shrink-0">{pf.meta && <Actions pf={pf} frameRef={frameRef} />}</div>
          </div>
          <PreviewPane pf={pf} frameRef={frameRef} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PrintFormButton({ label = "Print form", testid = "print-form-button", size = "sm", variant = "outline", ...ctx }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  if (user?.role === "auditor") return null;
  return (
    <>
      <Button size={size} variant={variant} onClick={() => setOpen(true)} data-testid={testid}>
        <Printer className="h-3.5 w-3.5 mr-1.5" /> {label}
      </Button>
      {open && <PrintFormDialog open={open} onOpenChange={setOpen} {...ctx} />}
    </>
  );
}
